import { permRecordNeedsInventoryStoreChoice } from "../../utils/inventoryStoreScope";

export type StoreYardOption = { id: string; name: string; sortOrder?: number };

type Props = {
  permRecord: Record<string, boolean>;
  yards: StoreYardOption[];
  yardsLoading?: boolean;
  allStores: boolean;
  selectedIds: string[];
  onChange: (next: { allStores: boolean; selectedIds: string[] }) => void;
};

export function InventoryStoreAccessSection({
  permRecord,
  yards,
  yardsLoading,
  allStores,
  selectedIds,
  onChange,
}: Props) {
  if (!permRecordNeedsInventoryStoreChoice(permRecord)) return null;

  const toggleYard = (id: string, on: boolean) => {
    const next = new Set(selectedIds);
    if (on) next.add(id);
    else next.delete(id);
    onChange({ allStores: false, selectedIds: [...next] });
  };

  const sorted = [...yards].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name));

  return (
    <fieldset className="console-users-page__perms inventory-store-access" aria-label="Inventory store / yard access">
      <legend className="permission-module-picker__legend">Inventory — store / yard access</legend>
      <p className="permission-module-picker__desc">
        This user has at least one inventory permission tied to stores (inward, outward, issue PO, issue material,
        store stocks, or goods receipts). Choose whether they may use <strong>all</strong> stores/yards or only the
        ones you select below.
      </p>
      {yardsLoading ? (
        <p className="text-muted" style={{ margin: "0.5rem 0" }}>
          Loading stores…
        </p>
      ) : sorted.length === 0 ? (
        <p className="text-muted" style={{ margin: "0.5rem 0" }}>
          No stores/yards found in the master list. Add stores under Masters → Stores &amp; yards first.
        </p>
      ) : (
        <>
          <label className="permission-module-picker__full-row inventory-store-access__radio-row">
            <input
              type="radio"
              name="inv-store-scope"
              checked={allStores}
              onChange={() => onChange({ allStores: true, selectedIds: [] })}
            />
            <span className="console-users-page__perm-label">All stores / yards (unrestricted inventory)</span>
          </label>
          <label className="permission-module-picker__full-row inventory-store-access__radio-row">
            <input
              type="radio"
              name="inv-store-scope"
              checked={!allStores}
              onChange={() => onChange({ allStores: false, selectedIds })}
            />
            <span className="console-users-page__perm-label">Only selected stores / yards</span>
          </label>
          {!allStores ? (
            <div className="inventory-store-access__grid" role="group" aria-label="Stores this user may access">
              {sorted.map((y) => (
                <label key={y.id} className="console-users-page__perm-row permission-module-picker__row">
                  <input
                    type="checkbox"
                    className="console-users-page__perm-check"
                    checked={selectedIds.includes(y.id)}
                    onChange={(e) => toggleYard(y.id, e.target.checked)}
                  />
                  <span className="console-users-page__perm-label">{y.name || y.id}</span>
                </label>
              ))}
            </div>
          ) : null}
        </>
      )}
    </fieldset>
  );
}
